import React from "react"
import { ReportLayout } from "../../../components/reports"
import Header from "../../../components/Header"

interface EventData {
  name: string
  startDate: Date
  endDate: Date
  venue: string
  location: string
  link: string
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]

const formatDateRange = (start: Date, end: Date): string => {
  const startDay = start.getDate()
  const endDay = end.getDate()
  const month = MONTH_NAMES[start.getMonth()]
  return `${month} ${startDay}-${endDay}, ${start.getFullYear()}`
}

const getMonth = (date: Date): string => MONTH_NAMES[date.getMonth()]

const events: EventData[] = [
  {
    name: "PCMA Convening Leaders",
    startDate: new Date(2026, 0, 11),
    endDate: new Date(2026, 0, 14),
    venue: "Pennsylvania Convention Center",
    location: "Philadelphia, USA",
    link: "https://conveningleaders.org/",
  },
  {
    name: "International Confex",
    startDate: new Date(2026, 1, 25),
    endDate: new Date(2026, 1, 26),
    venue: "ExCeL London",
    location: "London, UK",
    link: "https://www.international-confex.com/",
  },
  {
    name: "SXSW (South by Southwest)",
    startDate: new Date(2026, 2, 12),
    endDate: new Date(2026, 2, 18),
    venue: "Austin Convention Center",
    location: "Austin, USA",
    link: "https://www.sxsw.com/",
  },
  {
    name: "SaaStr Annual (AI Summit)",
    startDate: new Date(2026, 4, 12),
    endDate: new Date(2026, 4, 14),
    venue: "San Mateo County Events Center",
    location: "San Francisco Bay Area, USA",
    link: "https://www.saastrannual.com/",
  },
  {
    name: "IMEX Frankfurt",
    startDate: new Date(2026, 4, 19),
    endDate: new Date(2026, 4, 21),
    venue: "Messe Frankfurt",
    location: "Frankfurt, Germany",
    link: "https://frankfurt.imexevents.com/newfront",
  },
  {
    name: "Middle East Event Show (MEES)",
    startDate: new Date(2026, 5, 9),
    endDate: new Date(2026, 5, 11),
    venue: "Dubai World Trade Centre",
    location: "Dubai, UAE",
    link: "https://informaconnect.com/middle-east-event-show/",
  },
  {
    name: "Cannes Lions",
    startDate: new Date(2026, 5, 22),
    endDate: new Date(2026, 5, 26),
    venue: "Palais des Festivals et des Congres",
    location: "Cannes, France",
    link: "https://www.canneslions.com/",
  },
  {
    name: "Cvent CONNECT",
    startDate: new Date(2026, 6, 13),
    endDate: new Date(2026, 6, 16),
    venue: "Music City Center",
    location: "Nashville, USA",
    link: "https://www.cvent.com/en/cvent-connect",
  },
  {
    name: "CEMA Summit",
    startDate: new Date(2026, 7, 9),
    endDate: new Date(2026, 7, 11),
    venue: "Toronto",
    location: "Toronto, Canada",
    link: "https://cemaonline.com/",
  },
  {
    name: "Qatar Event Show",
    startDate: new Date(2026, 8, 1),
    endDate: new Date(2026, 8, 3),
    venue: "Doha Exhibition & Convention Center",
    location: "Doha, Qatar",
    link: "https://qatareventshow.com/",
  },
  {
    name: "Saudi Event Show (SES)",
    startDate: new Date(2026, 8, 9),
    endDate: new Date(2026, 8, 10),
    venue: "Riyadh",
    location: "Riyadh, Saudi Arabia",
    link: "https://informaconnect.com/saudi-event-show/",
  },
  {
    name: "Inbound by HubSpot",
    startDate: new Date(2026, 8, 16),
    endDate: new Date(2026, 8, 18),
    venue: "Boston Convention & Exhibition Center",
    location: "Boston, USA",
    link: "https://www.inbound.com/",
  },
  {
    name: "IMEX America",
    startDate: new Date(2026, 9, 13),
    endDate: new Date(2026, 9, 15),
    venue: "Mandalay Bay Convention Center",
    location: "Las Vegas, USA",
    link: "https://america.imexevents.com/",
  },
  {
    name: "Web Summit",
    startDate: new Date(2026, 10, 9),
    endDate: new Date(2026, 10, 12),
    venue: "MEO Arena",
    location: "Lisbon, Portugal",
    link: "https://websummit.com/",
  },
  {
    name: "Event Tech Live (ETL)",
    startDate: new Date(2026, 10, 11),
    endDate: new Date(2026, 10, 12),
    venue: "ExCeL London",
    location: "London, UK",
    link: "https://eventtechlive.com/etl-london-2026/",
  },
  {
    name: "IBTM World",
    startDate: new Date(2026, 10, 17),
    endDate: new Date(2026, 10, 19),
    venue: "Fira Barcelona Gran Via",
    location: "Barcelona, Spain",
    link: "https://www.ibtmworld.com/",
  },
].sort((a, b) => a.startDate.getTime() - b.startDate.getTime())

const EventCalendar2026 = () => {
  const REPORT_ID = "2026-event-calendar"

  return (
    <ReportLayout
      title="2026 Master Event Calendar for Event Professionals"
      description="Complete list of must-attend events for event professionals in 2026. From IMEX to Web Summit - plan your year with our comprehensive event calendar."
      reportId={REPORT_ID}
      reportTitle="2026 Master Event Calendar"
      reportSubtitle="Must-Attend Events for Event Professionals"
      showHeader={false}
      showPrintButton={true}
      showShareButton={true}
      keywords={[
        "event industry calendar 2026",
        "event professional conferences",
        "MICE industry events",
        "event technology conferences",
        "trade show calendar 2026",
        "IMEX 2026",
        "event marketing conferences",
        "hospitality events 2026",
      ]}
      meta={[
        {
          name: "keywords",
          content:
            "event calendar 2026, event industry events, MICE conferences, trade shows 2026, event professional calendar",
        },
        { name: "robots", content: "index, follow" },
        { name: "author", content: "Premagic" },
        {
          name: "og:title",
          content:
            "2026 Master Event Calendar for Event Professionals | Premagic",
        },
        {
          name: "og:description",
          content:
            "Complete list of must-attend events for event professionals in 2026. Plan your year with our comprehensive event calendar.",
        },
        { name: "og:type", content: "article" },
        {
          name: "og:url",
          content: "https://premagic.com/reports/2026-event-calendar/",
        },
        { name: "twitter:card", content: "summary_large_image" },
        {
          name: "twitter:title",
          content: "2026 Master Event Calendar for Event Professionals",
        },
        {
          name: "twitter:description",
          content:
            "Complete list of must-attend events for event professionals in 2026.",
        },
      ]}
    >
      <style>{`
        /* Override report container constraints for full-width layout */
        .report-container {
          max-width: 100% !important;
          padding: 0 !important;
          overflow: visible !important;
        }
        
        .calendar-page {
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          background: #fafbfc;
          min-height: 100vh;
        }
        
        .calendar-hero {
          background: linear-gradient(135deg, #1F0F61 0%, #4f46e5 100%);
          padding: 4rem 1rem;
          text-align: center;
          color: white;
          width: 100%;
        }
        
        .calendar-hero h1 {
          font-size: 2rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          color: white;
        }
        
        .calendar-hero p {
          font-size: 1.125rem;
          opacity: 0.9;
          max-width: 600px;
          margin: 0 auto;
          color: white;
        }
        
        .calendar-badge {
          display: inline-block;
          background: rgba(255, 255, 255, 0.2);
          padding: 0.25rem 0.75rem;
          border-radius: 9999px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 1rem;
        }
        
        .calendar-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 2rem 1rem;
        }
        
        .calendar-stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
          margin-bottom: 2rem;
        }
        
        .stat-card {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          text-align: center;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
          border: 1px solid #e5e7eb;
        }
        
        .stat-card .number {
          font-size: 2rem;
          font-weight: 700;
          color: #1F0F61;
          display: block;
        }
        
        .stat-card .label {
          font-size: 0.875rem;
          color: #6b7280;
          margin-top: 0.25rem;
        }
        
        .calendar-table-wrapper {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
          border: 1px solid #e5e7eb;
        }
        
        .calendar-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.9375rem;
        }
        
        .calendar-table th {
          background: #1F0F61;
          color: white;
          font-weight: 600;
          text-align: left;
          padding: 1rem 1.25rem;
          font-size: 0.8125rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        
        .calendar-table td {
          padding: 1rem 1.25rem;
          border-bottom: 1px solid #f3f4f6;
          vertical-align: middle;
        }
        
        .calendar-table tr:last-child td {
          border-bottom: none;
        }
        
        .calendar-table tr:hover td {
          background: #f9fafb;
        }
        
        .event-month {
          font-weight: 600;
          color: #4f46e5;
          white-space: nowrap;
        }
        
        .event-name {
          font-weight: 600;
          color: #1F0F61;
        }
        
        .event-date {
          color: #4b5563;
          white-space: nowrap;
        }
        
        .event-venue {
          color: #6b7280;
        }
        
        .event-location {
          color: #6b7280;
          display: flex;
          align-items: center;
          gap: 0.375rem;
        }
        
        .event-link {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          color: #4f46e5;
          font-weight: 500;
          text-decoration: none;
          transition: color 0.15s;
        }
        
        .event-link:hover {
          color: #1F0F61;
          text-decoration: underline;
        }
        
        .event-link svg {
          width: 1rem;
          height: 1rem;
        }
        
        .calendar-footer {
          margin-top: 2rem;
          padding: 1.5rem;
          background: #f1f4ff;
          border-radius: 12px;
          text-align: center;
        }
        
        .calendar-footer p {
          color: #4b5563;
          font-size: 0.875rem;
          margin: 0;
        }
        
        .calendar-footer strong {
          color: #1F0F61;
        }
        
        .print-footer {
          display: none;
        }
        
        @media (max-width: 768px) {
          .calendar-hero h1 {
            font-size: 1.5rem;
          }
          
          .calendar-hero p {
            font-size: 1rem;
          }
          
          .calendar-stats {
            grid-template-columns: 1fr;
          }
          
          .calendar-table-wrapper {
            overflow-x: auto;
          }
          
          .calendar-table {
            min-width: 800px;
          }
          
          .calendar-table th,
          .calendar-table td {
            padding: 0.75rem 1rem;
          }
        }
        
        @media print {
          @page {
            size: A4 portrait;
            margin: 20mm 15mm 25mm 15mm;
          }
          
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          
          html, body {
            font-size: 11pt;
            line-height: 1.5;
            color: #1a1a1a;
            background: white !important;
          }
          
          .no-print,
          header,
          nav,
          .calendar-stats,
          .calendar-footer,
          .event-location svg,
          .calendar-table th:last-child,
          .calendar-table td:last-child {
            display: none !important;
          }
          
          .report-container,
          .calendar-page,
          .calendar-content {
            max-width: 100% !important;
            padding: 0 !important;
            margin: 0 !important;
            background: white !important;
            overflow: visible !important;
          }
          
          .calendar-hero {
            background: #1F0F61 !important;
            padding: 25pt 20pt !important;
            margin: 0 0 20pt 0 !important;
            text-align: left !important;
          }
          
          .calendar-badge {
            background: rgba(255, 255, 255, 0.2) !important;
            color: white !important;
            font-size: 8pt;
            padding: 3pt 10pt;
            margin-bottom: 8pt;
          }
          
          .calendar-hero h1 {
            font-size: 24pt !important;
            font-weight: 700;
            color: white !important;
            margin: 0 0 6pt 0;
          }
          
          .calendar-hero p {
            font-size: 11pt !important;
            color: rgba(255, 255, 255, 0.9) !important;
            margin: 0;
          }
          
          .calendar-table-wrapper {
            border: 1pt solid #e5e7eb !important;
            box-shadow: none !important;
            border-radius: 0 !important;
            overflow: visible !important;
            margin-bottom: 15pt !important;
          }
          
          .calendar-table {
            width: 100% !important;
            border-collapse: collapse;
            font-size: 9.5pt;
          }
          
          .calendar-table thead {
            display: table-header-group;
          }
          
          .calendar-table tr {
            page-break-inside: avoid;
          }
          
          .calendar-table th {
            background: #1F0F61 !important;
            color: white !important;
            font-size: 8pt;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5pt;
            padding: 10pt 12pt !important;
            text-align: left;
          }
          
          .calendar-table td {
            padding: 10pt 12pt !important;
            border-bottom: 0.5pt solid #e5e7eb !important;
            vertical-align: middle;
          }
          
          .calendar-table tr:nth-child(even) td {
            background: #f9fafb !important;
          }
          
          .calendar-table tr:last-child td {
            border-bottom: none !important;
          }
          
          .event-month {
            font-weight: 600;
            color: #4f46e5 !important;
            font-size: 9pt;
            width: 14%;
          }
          
          .event-name {
            font-weight: 600;
            color: #1F0F61 !important;
            font-size: 9.5pt;
            width: 26%;
          }
          
          .event-date {
            color: #374151 !important;
            font-size: 9pt;
            width: 22%;
          }
          
          .event-venue {
            color: #4b5563 !important;
            font-size: 9pt;
            width: 22%;
          }
          
          .event-location {
            color: #4b5563 !important;
            font-size: 9pt;
            width: 16%;
          }
          
          .print-footer {
            display: block !important;
            text-align: center;
            font-size: 8pt;
            color: #6b7280;
            border-top: 0.5pt solid #d1d5db;
            padding-top: 10pt;
            margin-top: 15pt;
          }
          
          .print-footer span {
            color: #4f46e5;
            font-weight: 600;
          }
        }
      `}</style>

      <div className="calendar-page">
        <div className="no-print">
          <Header />
        </div>

        <div className="calendar-hero">
          <span className="calendar-badge">Resource Guide</span>
          <h1>2026 Master Event Calendar</h1>
          <p>
            Your comprehensive guide to the must-attend events for event
            professionals worldwide.
          </p>
        </div>

        <div className="calendar-content">
          <div className="calendar-stats">
            <div className="stat-card">
              <span className="number">{events.length}</span>
              <span className="label">Events Listed</span>
            </div>
            <div className="stat-card">
              <span className="number">12</span>
              <span className="label">Countries</span>
            </div>
            <div className="stat-card">
              <span className="number">12</span>
              <span className="label">Months Covered</span>
            </div>
          </div>

          <div className="calendar-table-wrapper">
            <table className="calendar-table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Event Name</th>
                  <th>Date</th>
                  <th>Venue</th>
                  <th>Location</th>
                  <th>Website</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event, index) => (
                  <tr key={index}>
                    <td className="event-month">{getMonth(event.startDate)}</td>
                    <td className="event-name">{event.name}</td>
                    <td className="event-date">
                      {formatDateRange(event.startDate, event.endDate)}
                    </td>
                    <td className="event-venue">{event.venue}</td>
                    <td className="event-location">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        style={{ width: 16, height: 16, flexShrink: 0 }}
                      >
                        <path
                          fillRule="evenodd"
                          d="M9.69 18.933l.003.001C9.89 19.02 10 19 10 19s.11.02.308-.066l.002-.001.006-.003.018-.008a5.741 5.741 0 00.281-.14c.186-.096.446-.24.757-.433.62-.384 1.445-.966 2.274-1.765C15.302 14.988 17 12.493 17 9A7 7 0 103 9c0 3.492 1.698 5.988 3.355 7.584a13.731 13.731 0 002.273 1.765 11.842 11.842 0 00.976.544l.062.029.018.008.006.003zM10 11.25a2.25 2.25 0 100-4.5 2.25 2.25 0 000 4.5z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {event.location}
                    </td>
                    <td>
                      <a
                        href={event.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="event-link"
                      >
                        Visit
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4.25 5.5a.75.75 0 00-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 00.75-.75v-4a.75.75 0 011.5 0v4A2.25 2.25 0 0112.75 17h-8.5A2.25 2.25 0 012 14.75v-8.5A2.25 2.25 0 014.25 4h5a.75.75 0 010 1.5h-5z"
                            clipRule="evenodd"
                          />
                          <path
                            fillRule="evenodd"
                            d="M6.194 12.753a.75.75 0 001.06.053L16.5 4.44v2.81a.75.75 0 001.5 0v-4.5a.75.75 0 00-.75-.75h-4.5a.75.75 0 000 1.5h2.553l-9.056 8.194a.75.75 0 00-.053 1.06z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="calendar-footer">
            <p>
              <strong>Note:</strong> Event dates and venues are subject to
              change. Please verify with official event websites before making
              travel arrangements. Data will be updated as more events are
              confirmed.
            </p>
          </div>

          <div className="print-footer">
            <span>premagic.com</span> &nbsp;•&nbsp; 2026 Master Event Calendar
            for Event Professionals &nbsp;•&nbsp; Dates subject to change
          </div>
        </div>
      </div>
    </ReportLayout>
  )
}

export default EventCalendar2026
